<script setup lang="ts">
import { RelayHub } from '@/views/Admin/Tenants/components';
import { onBeforeMount, onBeforeUnmount, ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { can, useMasterTenant, useRouting, useTenant } from '@/composables';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { useTenantIntegrationStore } from '@/store';
import Authentication from '@/views/Admin/Tenants/components/Integrations/Authentication';
import Quickbooks from '@/views/Admin/Tenants/components/Integrations/Quickbooks';
import UploadRSM from '@/views/Admin/Tenants/components/Integrations/UploadRSM';
import Screeners from '@/views/Admin/Tenants/components/Integrations/Screeners';
import StudentInformationSystem from '@/views/Admin/Tenants/components/Integrations/StudentInformationSystem';

const tenantIntegrationStore = useTenantIntegrationStore();
const { integrations, initialLoading } = storeToRefs(tenantIntegrationStore);

const $routing = useRouting();

const currentView = ref('authentication');

const hasQuickbooksAccess = ref(false);

const tenantId = ref(0);

const isMasterTenant = computed(() => useMasterTenant().isMaster());

const $tenant = useTenant();

const isMasterTenantAndMedicaidEnabled = computed(
  () => !!isMasterTenant.value && $tenant.tenantSettingEnabled(TENANT_SETTINGS.MEDICAID_BILLING),
);

onBeforeMount(async () => {
  tenantId.value = Number($routing.route.params.id);

  if (!integrations.value.length && !initialLoading.value) {
    await tenantIntegrationStore.getRoles(tenantId.value);
    await tenantIntegrationStore.getUsers(tenantId.value);
    await tenantIntegrationStore.getTenantIntegrations(tenantId.value);
  }

  updateCurrentView();
  window.addEventListener('hashchange', updateCurrentView);
  if (isMasterTenant.value && can([PERMISSIONS.QUICKBOOKS])) hasQuickbooksAccess.value = true;
});

onBeforeUnmount(async () => (integrations.value = []));

const updateCurrentView = () => (currentView.value = window.location.hash.replace('#', '') || 'authentication');
</script>

<template>
  <div class="w-full flex flex-row">
    <div class="col-fit h-min bg-white p-3 border-round-2xl">
      <ul class="list-none p-0">
        <li class="flex items-center border-bottom-2 border-100">
          <a
            class="p-3 font-semibold no-underline"
            :class="currentView === 'authentication' ? 'text-color-green' : 'text-500'"
            href="#authentication"
            v-text="'Authentication'"
          />
        </li>
        <li v-if="isMasterTenantAndMedicaidEnabled" class="flex items-center border-bottom-2 border-100">
          <a
            class="p-3 font-semibold no-underline"
            :class="currentView === 'relayhub' ? 'text-color-green' : 'text-500'"
            href="#relayhub"
          >
            Medicaid (RelayHub)
          </a>
        </li>
        <li v-if="hasQuickbooksAccess" class="flex border-bottom-2 border-100">
          <a
            class="p-3 font-semibold no-underline"
            :class="currentView === 'quickbooks' ? 'text-color-green' : 'text-500'"
            href="#quickbooks"
            v-text="'Quickbooks'"
          />
        </li>
        <li v-if="isMasterTenant" class="flex border-bottom-2 border-100">
          <a
            class="p-3 font-semibold no-underline"
            :class="currentView === 'rsm-upload' ? 'text-color-green' : 'text-500'"
            href="#rsm-upload"
            v-text="'RSM'"
          />
        </li>
        <li class="flex border-bottom-2 border-100">
          <a
            class="p-3 font-semibold no-underline"
            :class="currentView === 'screeners' ? 'text-color-green' : 'text-500'"
            href="#screeners"
            v-text="'Screeners (Resonant Ed)'"
          />
        </li>
        <li class="flex">
          <a
            class="p-3 font-semibold no-underline"
            :class="currentView === 'sis' ? 'text-color-green' : 'text-500'"
            href="#sis"
            v-text="'Student Information Systems (SIS)'"
          />
        </li>
      </ul>
    </div>
    <div v-if="!initialLoading" class="col-8 p-0 ml-4">
      <div v-if="currentView === 'quickbooks' && hasQuickbooksAccess">
        <Quickbooks />
      </div>
      <div v-else-if="currentView === 'authentication'">
        <Authentication />
      </div>
      <div v-else-if="currentView === 'rsm-upload' && isMasterTenant">
        <UploadRSM />
      </div>
      <div v-else-if="currentView === 'relayhub' && isMasterTenantAndMedicaidEnabled">
        <RelayHub />
      </div>
      <div v-else-if="currentView === 'screeners'">
        <Screeners />
      </div>
      <div v-else-if="currentView === 'sis'">
        <StudentInformationSystem />
      </div>
    </div>
  </div>
</template>
